import {
    EzBadParamException,
    EzBadStateException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzString,
    EzFunction,
    EzJson,
    EzPromise,
    EzArray
} from '/ezlibrary/helpers/EzHelpers.js';

import {
    EzEnvironment,
    EzRegistrationState,
    EzFeatureToggleId,
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzClass } from '/ezlibrary/EzClass.js';
import { EzEventEngine } from '/ezlibrary/ez-event-engine.js';

import { EzHttpHelper } from '/public/javascript/common/ezclocker-http-helper.js';
import { EzNavigation } from '/public/javascript/common/ezclocker-navigation-helper.js';

import { EzUrlHelper } from '/public/javascript/common/ezclocker-url-helper2.js';

import defaultFeatureToggles from '/dynamic/feature-toggles-loc.json';


/**
 * @class
 * @description
 * Provides data driven UX feature toggling
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Feature Id Format: esf{feature_name}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Tag features in HTML with:
 *      <div
 *          data-feature-id="ezf{feature_name}"
 *          id="Ez{feature_name}FeatureContainer"
 *          class="ezFeatureContainer"
 *          style="display:none">
 *          {feature_html}
 *      </div>
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * For JS feature toggles:
 *      // Using call backs
 *      globalThis.ezApi.ezclocker.ezFeatureToggles.ezExecuteIfFeatureEnabled(
 *          {viewName},
 *          esf{feature_name},
 *          function() {
 *              // Feature enabled code here
 *          },
 *          function () {
 *              // optional feature disabled code here
 *          });
 *
 *      // Using if statements
 *      if (globalThis.ezApi.ezclocker.ezFeatureToggles.ezViewFeatureEnabled({viewName}, esf{feature_name})) {
 *          // feature enabled code here
 *      }
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzFeatureToggles } from '/ezlibrary/ez-feature-toggles2.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Singleton instance ready for use when the below evaluates to true:
 *     globalThis.ezApi.ezclocker?.[EzFeatureToggles.ezApiName]?.ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Listen to onReady event:
 *     EzClass.ezWaitReady(
 *         EzFeatureToggles.ezEventNames.onReady,
 *         this.ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Singleton Instance Access:
 *     From within this class: EzFeatureToggles.ezInstance
 *     From outside of this class: globalThis.ezApi.ezclocker.ezFeatureToggles
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzFeatureToggles extends EzClass {
    /**
     * @static
     * @private @field
     * Stores the default feature toggle object
     *
     * This currently always used for local development
     *
     * @type {object}
     */
    static #defaultFeatures = defaultFeatureToggles || {
        views: {
            ezEmployerTimeOffView: {
                ezfTimeOff: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezEmployerTimeOffView),
                    enabled: true,
                    featureOn: true
                }
            },
            ezEmployeerTimeOffView: {
                ezfTimeOff: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezEmployeerTimeOffView),
                    enabled: true,
                    featureOn: true
                }
            },
            ezTeamChatDialog: {
                ezfTeamChat: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezTeamChatDialog),
                    enabled: true,
                    featureOn: true
                }
            },
            ezEmployerAccountDetailsView: {
                ezfYearlySubscriptions2023: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezEmployerAccountDetailsView),
                    enabled: true,
                    featureOn: true
                },
                ezfEZOPTION_REQUIRE_LOCATION_FOR_CLOCKINOUT: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEZOPTION_REQUIRE_LOCATION_FOR_CLOCKINOUT),
                    enabled: true,
                    featureOn: true
                },
                ezfIntegrations: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfIntegrations),
                    enabled: true,
                    featureOn: true
                },
                ezScheduleDrivenClockIn: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezScheduleDrivenClockIn),
                    enabled: true,
                    featureOn: true
                },
                ezfEZOPTION_ALLOW_RECORDING_OF_UNPAID_BREAKS: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEZOPTION_ALLOW_RECORDING_OF_UNPAID_BREAKS),
                    enabled: true,
                    featureOn: true
                },
                ezfEZOPTION_ALLOW_AUTOMATIC_BREAKS: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEZOPTION_ALLOW_AUTOMATIC_BREAKS),
                    enabled: true,
                    featureOn: true
                },
                ezfEZOPTION_ALLOW_EMPLOYEES_TO_SEE_COWORKER_SCHEDULES: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEZOPTION_ALLOW_EMPLOYEES_TO_SEE_COWORKER_SCHEDULES),
                    enabled: true,
                    featureOn: true
                },
                ezfEZOPTION_ALLOW_EMPLOYEES_TO_CLAIM_OPEN_SHIFTS: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEZOPTION_ALLOW_EMPLOYEES_TO_CLAIM_OPEN_SHIFTS),
                    enabled: true,
                    featureOn: true
                },
                ezfEZOPTION_ALLOW_TIME_OFF_REQUESTS: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEZOPTION_ALLOW_TIME_OFF_REQUESTS),
                    enabled: true,
                    featureOn: true
                },
                ezRoundClockInClockOut: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezRoundClockInClockOut),
                    enabled: true,
                    featureOn: true
                },
                ezfTimeOff: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfTimeOff),
                    enabled: true,
                    featureOn: true
                },
                ezfTeamChat: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfTeamChat),
                    enabled: true,
                    featureOn: true
                },
                ezfEZOPTION_RESTRICT_CLOCK_IN_OUT_TO_RADIUS_WITHIN_JOB_LOCATION: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEZOPTION_RESTRICT_CLOCK_IN_OUT_TO_RADIUS_WITHIN_JOB_LOCATION),
                    enabled: true,
                    featureOn: true
                },
                ezfEZOPTION_CUSTOM_PERIOD_START_TIME: {
                    groupName: "ezfgEzClockerTimePeriodFeatures",
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEZOPTION_CUSTOM_PERIOD_START_TIME),
                    enabled: true,
                    featureOn: true
                },
                ezfEZOPTION_NOTIFY_EMPLOYEE_MEAL_BREAK_AFTER_HOURS: {
                    groupName: "ezfgEzClockerLegacyNotifications",
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEZOPTION_NOTIFY_EMPLOYEE_MEAL_BREAK_AFTER_HOURS),
                    enabled: true,
                    featureOn: true
                },
                ezfEZOPTION_NOTIFY_EMPLOYER_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT: {
                    groupName: "ezfgEzClockerNotificationServiceFeatures",
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEZOPTION_NOTIFY_EMPLOYER_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT),
                    "enabled": true,
                    "featureOn": true,
                    "employerIds": [
                        // Pre-production test account: boogers.boats@mailinator.com, 12341234
                        "eId1565",
                        // Pre-production test account: ez.test.jobs@mailinator.com, 1234
                        "eId535",
                        // CTO Local Development Test Account: boogers.boats@mailinator.com, 12341234
                        "eId2"
                    ]
                },
                ezfEZOPTION_NOTIFY_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT: {
                    groupName: "ezfgEzClockerNotificationServiceFeatures",
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEZOPTION_NOTIFY_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT),
                    "enabled": true,
                    "featureOn": true,
                    "employerIds": [
                        // Pre-production test account: boogers.boats@mailinator.com, 12341234
                        "eId1565",
                        // Pre-production test account: ez.test.jobs@mailinator.com, 1234
                        "eId535",
                        // CTO Local Development Test Account: boogers.boats@mailinator.com, 12341234
                        "eId2"
                    ]
                },
                ezfRemindEmployeesBeforeShiftStarts: {
                    groupName: "ezfgEzClockerNotificationServiceFeatures",
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfRemindEmployeesBeforeShiftStarts),
                    enabled: true,
                    featureOn: true,
                    employerIds: [
                        // Pre-production test account: boogers.boats@mailinator.com, 12341234
                        "eId1565",
                        // Pre-production test account: ez.test.jobs@mailinator.com, 1234
                        "eId535"
                    ]
                },
                ezfEZOPTION_SEND_SCHEDULE_NOTIFICATIONS: {
                    groupName: "ezfgEzClockerNotificationServiceFeatures",
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEZOPTION_SEND_SCHEDULE_NOTIFICATIONS),
                    enabled: true,
                    featureOn: true,
                    employerIds: [
                        // Pre-production test account: boogers.boats@mailinator.com, 12341234
                        "eId1565",
                        // Pre-production test account: ez.test.jobs@mailinator.com, 1234
                        "eId535",
                        // CTO Local Development Test Account: boogers.boats@mailinator.com, 12341234
                        "eId2"
                    ]
                },
                "ezfEzClockerNotificationService": {
                    groupName: "ezfgEzClockerNotificationServiceFeatures",
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEzClockerNotificationService),
                    enabled: true,
                    featureOn: true,
                    employerIds: [
                        // Pre-production test account: boogers.boats@mailinator.com, 12341234
                        "eId1565",
                        // Pre-production test account: ez.test.jobs@mailinator.com, 1234
                        "eId535",
                        // CTO Local Development Test Account: boogers.boats@mailinator.com, 12341234
                        "eId2"
                    ]
                }
            },
            employerDashboard: {
                ezfActiveTimeZoneDisplay: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfActiveTimeZoneDisplay),
                    enabled: true,
                    featureOn: true
                },
                ezfIntegrations: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfIntegrations),
                    enabled: true,
                    featureOn: true
                },
                JOBS: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.JOBS),
                    enabled: true,
                    featureOn: true
                },
                ezfEmployePdfReport: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEmployePdfReport),
                    enabled: true,
                    featureOn: true,
                    style: 'display:grid;'
                },
                ezfTimeOff: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfTimeOff),
                    enabled: true,
                    featureOn: true
                },
                ezfTeamChat: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfTeamChat),
                    enabled: true,
                    featureOn: true
                },
                ezfEzClockerLongRunningReportsService: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEzClockerLongRunningReportsService),
                    enabled: true,
                    featureOn: true
                },
                ezReferAFriend: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezReferAFriend),
                    enabled: true,
                    featureOn: true
                },
                ezfShowTimeEntryImages: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfShowTimeEntryImages),
                    enabled: true,
                    featureOn: true
                },
                ezHelpAiChat: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezHelpAiChat),
                    enabled: true,
                    featureOn: true
                },
            },
            employerSchedule: {
                ezfActiveTimeZoneDisplay: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfActiveTimeZoneDisplay),
                    enabled: true,
                    featureOn: true
                },
                ezfPublishSchedule: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfPublishSchedule),
                    enabled: true,
                    featureOn: true
                },
                ezfExportSchedule: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfExportSchedule),
                    enabled: true,
                    featureOn: true
                },
                ezfTimeOff: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfTimeOff),
                    enabled: true,
                    featureOn: true
                },
                ezfTeamChat: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfTeamChat),
                    enabled: true,
                    featureOn: true
                },
                ezfEZOPTION_SEND_SCHEDULE_NOTIFICATIONS: {
                    groupName: "ezfgEzClockerNotificationServiceFeatures",
                    description: "Feature to send notifications for schedule add/update using the EzClockerNotificationService.",
                    enabled: false,
                    featureOn: false,
                    employerIds: [
                        // Pre-production test account: boogers.boats@mailinator.com, 12341234
                        "eId1565",
                        // Pre-production test account: ez.test.jobs@mailinator.com, 1234
                        "eId535",
                        // CTO Local Development Test Account: boogers.boats@mailinator.com, 12341234
                        "eId2"
                    ]
                },
                ezfEzClockerNotificationService: {
                    groupName: "ezfgEzClockerNotificationServiceFeatures",
                    description: "Feature to send notifications using the EzClockerNotificationService.",
                    enabled: false,
                    featureOn: false,
                    employerIds: [
                        // Pre-production test account: boogers.boats@mailinator.com, 12341234
                        "eId1565",
                        // Pre-production test account: ez.test.jobs@mailinator.com, 1234
                        "eId535",
                        // CTO Local Development Test Account: boogers.boats@mailinator.com, 12341234
                        "eId2"
                    ]
                }
            },
            employeeDashboard: {
                ezfTimeOff: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.employeeDashboard),
                    enabled: true,
                    featureOn: true
                },
                ezfTeamChat: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfTeamChat),
                    enabled: true,
                    featureOn: true
                },
                ezfEzClockerLongRunningReportsService: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfEzClockerLongRunningReportsService),
                    enabled: true,
                    featureOn: false
                },
                ezfShowTimeEntryImages: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfShowTimeEntryImages),
                    enabled: true,
                    featureOn: true
                }
            },
            employeeSchedule: {
                ezfExportSchedule: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfExportSchedule),
                    enabled: true,
                    featureOn: true
                },
                ezfTimeOff: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfTimeOff),
                    enabled: true,
                    featureOn: true
                },
                ezfTeamChat: {
                    metaData: EzFeatureToggleId.ezEnumData(EzFeatureToggleId.ezfTeamChat),
                    enabled: true,
                    featureOn: true
                }
            }
        }
    };
    /**
     * @static
     * @public @readonly @property
     * Gets the default feature toggle instance
     *
     * @returns {object}
     */
    static get DEFAULT_FEATURES() {
        return this.#defaultFeatures;
    }

    /**
     * @public @field
     * Stores if debug mode is enabled or not
     * @type {boolean}
     */
    ezDebugMode = false;

    /**
     * @public @field
     * Stores a reference to the feature toggle data
     *
     * @type {object}
     */
    ezFeatures = EzFeatureToggles.DEFAULT_FEATURES;

    /**
     * @public @field
     * Stores the current deployment enviornment
     *
     * @type {string}
     * A valid enumeration property value from EzEnvironment
     */
    ezEnvironment = EzEnvironment.PRD;

    /**
     * @public @field
     * Stores the name of the current view
     *
     * @type {string}
     */
    ezCurrentView = EzString.EMPTY;

    /**
     * @public @field
     * Stores an alternate reference to the static property EzFeatureToggles.ezEventNames
     *
     * @type {object}
     * @deprecated
     * Migrate to using the static property: EzFeatureToggles.ezEventNames
     */
    ezEventNames = EzFeatureToggles.ezEventNames;

    /**
     * @public @method
     * Returns the full ezClocker dynamic/{pagename} url
     *
     * @param {string} pageName
     *
     * @returns {string}
     */
    ezGetDynamicPageUrl(pageName) {
        return `/dynamic/${pageName}`;
    }

    /**
     * @public @method
     * Initializes EzFeatureToggles
     *
     * @returns {EzFeatureToggles}
     */
    ezInit() {
        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzFeatureToggles.ezApiName,
            EzFeatureToggles.ezEventNames.onFeatureTogglesReady);

        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzFeatureToggles.ezApiName,
            EzFeatureToggles.ezEventNames.onFeatureTogglesApplied);

        EzFeatureToggles.ezInstance.ezGetEnvironment()
            .then(EzFeatureToggles.ezInstance.ezLoadFeatures)
            .then(
                () => {
                    globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                        EzFeatureToggles.ezEventNames.onFeatureTogglesReady,
                        globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                            EzFeatureToggles.ezApiName,
                            'EzFeatureToggles is ready.',
                            EzFeatureToggles.ezInstance
                        ));
                });

        return EzFeatureToggles.ezInstance;
    }

    /**
     * @public @method
     * Obtains the enviornment by evaluating the current url
     *
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezUrlHelper.ezGetEnvironmentFromUrl()
     */
    ezGetEnvironmentFromUrl() {
        const url = window.location.href;

        if (url.includes('://localhost') ||
            url.includes('://127.0.0.1') ||
            url.includes('://loc.ezclocker.com') ||
            url.includes('://loc-app.ezclocker.com') ||
            url.includes('://loc-api.ezclocker.com')) {
            return EzEnvironment.LOC;
        }

        if (url.includes('https://dev.ezclocker.com') ||
            url.includes('https://dev-app.ezclocker.com') ||
            url.includes('https://dev-api.ezclocker.com') ||
            url.includes('https://dev1.ezclocker.com') ||
            url.includes('https://dev1-app.ezclocker.com') ||
            url.includes('https://dev1-api.ezclocker.com') ||
            url.includes('https://dev2.ezclocker.com') ||
            url.includes('https://dev2-app.ezclocker.com') ||
            url.includes('https://dev2-api.ezclocker.com')) {
            return EzEnvironment.DEV;
        }

        if (url.includes('https://qal.ezclocker.com') ||
            url.includes('https://qal-app.ezclocker.com') ||
            url.includes('https://qal-api.ezclocker.com')) {
            return EzEnvironment.QAL;
        }

        if (url.includes('https://e2e.ezclocker.com') ||
            url.includes('https://e2e-app.ezclocker.com') ||
            url.includes('https://e2e-api.ezclocker.com')) {
            return EzEnvironment.E2E;
        }

        if (url.includes('https://prf.ezclocker.com') ||
            url.includes('https://prf-app.ezclocker.com') ||
            url.includes('https://prf-api.ezclocker.com')) {
            return EzEnvironment.PRF;
        }

        if (url.includes('https://pqa.ezclocker.com') ||
            url.includes('https://pqa-app.ezclocker.com') ||
            url.includes('https://pqa-api.ezclocker.com')) {
            return EzEnvironment.PQA;
        }

        if (url.includes('https://stage.ezclocker.com') ||
            url.includes('https://stg.ezclocker.com') ||
            url.includes('https://stg-app.ezclocker.com') ||
            url.includes('https://stg-api.ezclocker.com') ||
            url.includes('https://stg-green.ezclocker.com') ||
            url.includes('https://stg-green-app.ezclocker.com') ||
            url.includes('https://stg-green-api.ezclocker.com') ||
            url.includes('https://stg-orange.ezclocker.com') ||
            url.includes('https://stg-orange-app.ezclocker.com') ||
            url.includes('https://stg-orange-api.ezclocker.com')) {
            return EzEnvironment.STG;
        }

        if (url.includes('https://ezclocker.com') ||
            url.includes('https://www.ezclocker.com') ||
            url.includes('https://api.ezclocker.com') ||
            url.includes('https://app.ezclocker.com') ||
            url.includes('https://green.ezclocker.com') ||
            url.includes('https://green-api.ezclocker.com') ||
            url.includes('https://green-app.ezclocker.com') ||
            url.includes('https://orange.ezclocker.com') ||
            url.includes('https://orange-api.ezclocker.com') ||
            url.includes('https://orange-app.ezclocker.com')) {
            return EzEnvironment.PRD;
        }

        // Always default to production if environment cannot be determined from url
        return EzEnvironment.PRD;
    }

    /**
     * @public @method
     * Obtains the current environment
     *
     * @returns {Promise.resolve}
     */
    ezGetEnvironment() {
        return EzPromise.asyncAction(
            (resolve) => globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
                globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicApiServiceUrl('version', 'v1'))
                .then(
                    (response) => {
                        const isStageGreenUrl = window.location.href.startsWith('https://stg-green.ezclocker.com') ||
                            window.location.href.startsWith('https://stg-green-app.ezclocker.com') ||
                            window.location.href.startsWith('https://stg-green-api.ezclocker.com');

                        const isStageOrangeUrl = window.location.href.startsWith('https://stg-orange.ezclocker.com') ||
                            window.location.href.startsWith('https://stg-orange-app.ezclocker.com') ||
                            window.location.href.startsWith('https://stg-orange-api.ezclocker.com');

                        if (isStageGreenUrl || isStageOrangeUrl) {
                            EzFeatureToggles.ezInstance.ezEnvironment = EzEnvironment.STG;
                        } else if (EzString.hasText(response.environment)) {
                            EzFeatureToggles.ezInstance.ezEnvironment = response.environment;
                        } else if (EzString.hasText(response.targetEnvironment)) {
                            EzFeatureToggles.ezInstance.ezEnvironment = response.targetEnvironment;
                        } else {
                            EzFeatureToggles.ezInstance.ezEnvironment = globalThis.ezApi.ezclocker.ezUrlHelper.ezGetEnvironmentFromUrl();
                        }

                        if (!EzString.hasText(EzFeatureToggles.ezInstance.ezEnvironment)) {
                            // Always default to production if environment cannot be determined
                            EzFeatureToggles.ezInstance.ezEnvironment = EzEnvironment.PRD;
                        }

                        return resolve(EzFeatureToggles.ezInstance.ezEnvironment);
                    },
                    (eResponse) => {
                        if (EzBoolean.isTrue(EzFeatureToggles.ezInstance.ezDebugMode)) {
                            globalThis.ezApi.ezclocker.ezLogger.error(
                                `Unable to determine the current deployment environment. Error: ${EzJson.toJson(eResponse)}`);
                        } else {
                            globalThis.ezApi.ezclocker.ezLogger.warn(
                                'EzFeatureToggles: Environment information was not available. Defaulting to enviornment PRD');
                        }

                        EzFeatureToggles.ezInstance.ezEnvironment = EzEnvironment.PRD;

                        return resolve(EzFeatureToggles.ezInstance.ezEnvironment);
                    }));
    }

    /**
     * @public @method
     * Obtains the dynamic features from S3
     *
     * @returns {Promise.resolve}
     */
    ezLoadFeatures() {
        return EzPromise.asyncAction(
            (resolve) => {
                const url = window.location.href;

                if (EzNumber.MINUS_ONE !== url.indexOf('://localhost:')) {
                    EzFeatureToggles.ezInstance.ezEnvironment = EzEnvironment.LOC;
                }

                let featureToggleFilename = `feature-toggles.json?v=${moment().format('x')}`;

                switch (EzFeatureToggles.ezInstance.ezEnvironment) {
                    case EzEnvironment.LOC:
                        // Use the embedded default for local
                        EzFeatureToggles.ezInstance.ezFeatures = EzFeatureToggles.DEFAULT_FEATURES;

                        return resolve();
                    case EzEnvironment.DEV:
                        featureToggleFilename = `feature-toggles-${EzFeatureToggles.ezInstance.ezEnvironment}.json?v=${moment().format('x')}`;

                        break;
                    case EzEnvironment.QAL:
                        featureToggleFilename = `feature-toggles-${EzFeatureToggles.ezInstance.ezEnvironment}.json?v=${moment().format('x')}`;

                        break;
                    case EzEnvironment.E2E:
                        featureToggleFilename = `feature-toggles-${EzFeatureToggles.ezInstance.ezEnvironment}.json?v=${moment().format('x')}`;

                        break;
                    case EzEnvironment.PRF:
                        featureToggleFilename = `feature-toggles-${EzFeatureToggles.ezInstance.ezEnvironment}.json?v=${moment().format('x')}`;

                        break;
                    case EzEnvironment.PRD:
                    default:
                        featureToggleFilename = EzEnvironment.STG === EzFeatureToggles.ezInstance.ezGetEnvironmentFromUrl()
                            ? `feature-toggles-${EzEnvironment.STG}.json?v=${moment().format('x')}`
                            : `feature-toggles.json?v=${moment().format('x')}`;

                        break;
                }

                return globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
                    EzFeatureToggles.ezInstance.ezGetDynamicPageUrl(featureToggleFilename))
                    .then(
                        (response) => {
                            EzFeatureToggles.ezInstance.ezFeatures = EzJson.fromJson(response);

                            return resolve();
                        },
                        (eResponse) => {
                            EzFeatureToggles.ezInstance.ezFeatures = EzFeatureToggles.DEFAULT_FEATURES;

                            if (EzBoolean.isTrue(EzFeatureToggles.ezInstance.ezDebugMode)) {
                                globalThis.ezApi.ezclocker.ezLogger.warn(
                                    EzString.em`
                                        Unable to load the feature toggle settings file ${featureToggleFilename}. Using the default
                                        settings instead. Response: ${EzJson.toJson(eResponse)}`);
                            }

                            return resolve();
                        });
            });
    }

    /**
     * @public @method
     * Determines if a view feature is enabled or not for JS use
     * @param {string} viewName
     * @param {string} featureId
     * @returns {boolean}
     */
    ezViewFeatureEnabled(viewName, featureId) {
        if (!EzObject.isValid(EzFeatureToggles.ezInstance.ezFeatures)) {
            return false;
        }
        if (!EzObject.hasProperty(EzFeatureToggles.ezInstance.ezFeatures.views, viewName)) {
            return false;
        }
        if (!EzObject.hasProperty(EzFeatureToggles.ezInstance.ezFeatures.views[viewName], featureId)) {
            return false;
        }

        return EzFeatureToggles.ezInstance.ezFeatures.views[viewName][featureId].enabled;
    }

    /**
     * @public @method
     * Determiens if a view feature is on or not
     * @param {string} viewName
     * @param {string} featureId
     * @returns {boolean}
     */
    ezIsViewFeatureOn(viewName, featureId) {
        if (!EzObject.isValid(EzFeatureToggles.ezInstance.ezFeatures)) {
            return false;
        }
        if (!EzObject.isValid(EzFeatureToggles.ezInstance.ezFeatures?.views?.[viewName])) {
            return false;
        }
        if (!EzObject.isValid(EzFeatureToggles.ezInstance.ezFeatures.views[viewName]?.[featureId])) {
            return false;
        }

        return EzFeatureToggles.ezInstance.ezViewFeatureEnabled(viewName, featureId) &&
            EzBoolean.booleanOrFalse(EzFeatureToggles.ezInstance.ezFeatures.views[viewName][featureId]?.featureOn);
    }

    /**
     * @public @method
     * Executes the provided executeFunction if the viewName's featureId is enabled. Otherwise, the didNotExecuteResult
     * is returned.
     *
     * @param {string} viewName
     * @param {string} featureId
     * @param {undefined|null|function} executeFunction
     * @param {*} didNotExecuteResult
     *
     * @returns {*}
     * Returns the result of the executeFunction called, or the didNotExecuteResult if the executeFunction was not called.
     */
    ezExecuteIfFeatureEnabled(viewName, featureId, executeFunction, didNotExecuteResult) {
        if (!EzString.hasLength(viewName)) {
            throw new EzBadParamException(
                'viewName',
                EzFeatureToggles.ezInstance,
                EzFeatureToggles.ezInstance.ezExecuteIfFeatureEnabled);
        }
        if (!EzString.hasLength(featureId)) {
            throw new EzBadParamException(
                'viewName',
                EzFeatureToggles.ezInstance,
                EzFeatureToggles.ezInstance.ezExecuteIfFeatureEnabled);
        }
        if (!EzFunction.isFunction(executeFunction)) {
            throw new EzBadParamException(
                'executeFunction',
                EzFeatureToggles.ezInstance,
                EzFeatureToggles.ezInstance.ezExecuteIfFeatureEnabled);
        }

        return EzFeatureToggles.ezInstance.ezViewFeatureEnabled(viewName, featureId)
            ? executeFunction()
            : didNotExecuteResult;
    }

    /**
     * @public @function
     * Determines if the provided featureId is enabled for the employer. Caches the result to avoid redundant calls to the API.
     * @param {string} featureId - The ID of the feature to check.
     * @return {boolean} - True if the feature is enabled for the employer, false otherwise.
     */
    ezViewFeatureEnabledForSelectedEmployer(viewName, featureId) {
        if (!EzString.hasText(viewName)) {
            throw new EzBadParamException(
                'viewName',
                EzFeatureToggles.ezInstance,
                EzFeatureToggles.ezInstance.ezIsFeatureEnabledForSelectedEmployer);
        }
        if (!EzString.hasText(featureId)) {
            throw new EzBadParamException(
                'featureId',
                EzFeatureToggles.ezInstance,
                EzFeatureToggles.ezInstance.ezIsFeatureEnabledForSelectedEmployer);
        }

        if (EzNumber.isNumber(globalThis.ezApi.ezclocker.ezClockerContext?.selectedEmployerAccount?.id)) {
            throw new EzBadStateException(
                'Expected a valid employer id from call to globalThis.ezApi.ezclocker.ezClockerContext?.selectedEmployerAccount?.id',
                'However, globalThis.ezApi.ezclocker.ezClockerContext?.selectedEmployerAccount?.id returns undefined, null, or is not a valid number.',
                EzFeatureToggles.ezInstance,
                EzFeatureToggles.ezInstance.ezIsFeatureEnabledForSelectedEmployer);
        }

        return EzFeatureToggles.ezInstance.ezViewFeatureEnabledForEmployer(
            viewName,
            featureId,
            globalThis.ezApi.ezclocker.ezClockerContext.selectedEmployerAccount.id);
    }

    /**
     * @public @function
     * Determines if the provided featureId is enabled for the employer. Caches the result to avoid redundant calls to the API.
     * @param {string} featureId - The ID of the feature to check.
     * @return {boolean} - True if the feature is enabled for the employer, false otherwise.
     */
    ezViewFeatureEnabledForActiveEmployer(viewName, featureId) {
        if (!EzString.hasText(viewName)) {
            throw new EzBadParamException(
                'viewName',
                EzFeatureToggles.ezInstance,
                EzFeatureToggles.ezInstance.ezIsFeatureEnabledForSelectedEmployer);
        }
        if (!EzString.hasText(featureId)) {
            throw new EzBadParamException(
                'featureId',
                EzFeatureToggles.ezInstance,
                EzFeatureToggles.ezInstance.ezIsFeatureEnabledForSelectedEmployer);
        }

        const activeEmployerId = EzNumber.numberOrNull(
            globalThis.ezApi.ezclocker.ezClockerContext?.activeEmployer?.id || globalThis.ezApi.ezclocker.ezClockerContext?.selectedEmployerAccount?.id);

        if (!EzNumber.isNumber(activeEmployerId) || EzNumber.ZERO > activeEmployerId) {
            throw new EzBadStateException(
                'Expected a valid employer id from call to globalThis.ezApi.ezclocker.ezClockerContext?.activeEmployer?.id',
                'However, globalThis.ezApi.ezclocker.ezClockerContext?.activeEmployer?.id returns undefined, null, not a valid number, or is less than zero.',
                EzFeatureToggles.ezInstance,
                EzFeatureToggles.ezInstance.ezIsFeatureEnabledForSelectedEmployer);
        }

        return EzFeatureToggles.ezInstance.ezViewFeatureEnabledForEmployer(
            viewName,
            featureId,
            activeEmployerId);
    }

    /**
     * @public @method
     * Determines if a specified feature id is enabled for the provided employerid
     * @param {string} viewName
     * @param {string} featureId
     * @param {number} employerId
     * @returns {boolean}
     */
    ezViewFeatureEnabledForEmployer(viewName, featureId, employerId) {
        if (!EzString.hasText(viewName)) {
            throw new EzBadParamException(
                'viewName',
                EzFeatureToggles.ezInstance,
                EzFeatureToggles.ezInstance.ezIsFeatureEnabledForSelectedEmployer);
        }
        if (!EzString.hasText(featureId)) {
            throw new EzBadParamException(
                'featureId',
                EzFeatureToggles.ezInstance,
                EzFeatureToggles.ezInstance.ezIsFeatureEnabledForSelectedEmployer);
        }
        if (!EzNumber.isNumber(employerId) || 0 > employerId) {
            throw new EzBadParamException(
                'employerId',
                EzFeatureToggles.ezInstance,
                EzFeatureToggles.ezInstance.ezIsFeatureEnabledForSelectedEmployer,
                'The employerId must be a valid number greater than or equal to zero.');
        }

        if (!EzFeatureToggles.ezInstance.ezViewFeatureEnabled(viewName, featureId) || !EzFeatureToggles.ezInstance.ezIsViewFeatureOn(viewName, featureId)) {
            // Feature is diabled or not on
            return false;
        }

        const feature = EzFeatureToggles.ezInstance.ezFeatures.views?.[viewName]?.[featureId];

        // Enabled for the employer id if the following are true:
        // 1. The employerIds property is an empty array, undefined, or null
        // 2. The employerIds property contains a key of 'ezId*' - meaning all employers
        // 3. The employerIds property contains a key equal to `ezId${employerId}` - specific employer id matches
        return EzArray.hasLength(feature?.employerIds)
            ? feature.employerIds.includes('eId*') || feature.employerIds.includes(`eId${employerId}`)
            : true;
    }

    /**
     * @public @method
     * Applies the needed actions for features associated with provided view name
     *
     * @param {String} viewName
     */
    ezApplyViewFeatureToggles(viewName) {
        const view = EzFeatureToggles.ezInstance.ezFeatures.views[viewName];

        if (!EzObject.isValid(view)) {
            // feature toggles not supported for view
            return;
        }

        for (const featureId in view) {
            if (view?.[featureId]) {
                const feature = view[featureId];

                const featureElements = document.querySelectorAll(`[data-feature-id="${featureId}"]`);

                featureElements.forEach(
                    (ele) => {
                        const msg = `[FEATURE TOGGLE][${EzJson.toJson(ele.id)}]:${featureId}=${EzJson.toJson(feature)}`;

                        globalThis.ezApi.ezclocker.ezLogger.debug(msg);

                        if (EzBoolean.isTrue(feature.enabled) && EzBoolean.isTrue(feature.featureOn)) {
                            const rawStyle = EzString.stringWithTextOrDefault(feature?.style, 'contents');

                            const styleMatch = rawStyle.match(/display\s*:\s*([^;]+)/i);

                            const featureEnabledStyle = styleMatch
                                ? styleMatch[EzNumber.SECOND_INDEX].trim()
                                : rawStyle;

                            if (EzArray.isArray(feature?.employerIds)) {
                                // Has employer id restrictions
                                const activeEmployerId = globalThis.ezApi.ezclocker.ezClockerContext?.activeEmployer?.id;

                                // Enable if employerId is in employerIds list or eId* is in employerIds list.
                                ele.style.display = feature.employerIds.includes('eId*') || feature.employerIds.includes(`eId${activeEmployerId}`)
                                    ? featureEnabledStyle
                                    : 'none';
                            } else {
                                // No employerid restrictions, enable feature
                                ele.style.display = featureEnabledStyle;
                            }
                        } else {
                            // Feature not enabled or not on
                            ele.style.display = 'none';
                        }
                    });
            }
        }

        globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
            EzFeatureToggles.ezEventNames.onFeatureTogglesApplied,
            globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                EzFeatureToggles.ezApiName,
                'EzFeatureToggles have applied.',
                EzFeatureToggles.ezInstance));
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @method @readonly @property
     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
     *
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezFeatureToggles';
    }

    /**
     * @static
     * @public @method @readonly @property
     * Gets an object of event names that this class may trigger.
     *
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzFeatureToggles_Ready',
            onFeatureTogglesReady: 'EzFeatureToggles_Ready',
            onFeatureTogglesApplied: 'EzFeatureToggles_Applied'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with globalThis.ezApi.
     *
     * @type {EzFeatureToggles}
     */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzFeatureToggles.ezApiName]
        ? globalThis.ezApi.ezclocker[EzFeatureToggles.ezApiName]
        : null;
    /**
     * @static
     * @public @method @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     *
     * @returns {EzFeatureToggles}
     */
    static get ezInstance() {
        return EzFeatureToggles.#ezInstance;
    }
    /**
     * @static
     * @public @method @property @getter
     * Sets the singleton instance of of this class that is registered with globalThis.ezApi.
     *
     * @param {EzFeatureToggles} ezFeatureToggles
     */
    static set ezInstance(ezFeatureToggles) {
        if (null != EzFeatureToggles.#ezInstance) {
            throw new Error('EzFeatureToggles\'s singleton instance is already reigstered with globalThis.ezApi.');
        }

        EzFeatureToggles.#ezInstance = ezFeatureToggles;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     *
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzFeatureToggles.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @method @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     *
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzFeatureToggles.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @method @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     *
     * @param {string}ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzFeatureToggles.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets true when all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     *
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzFeatureToggles.ezApiRegistrationState && globalThis?.ezApi?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzEventEngine.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzHttpHelper.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzNavigation.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzUrlHelper.ezApiName]?.ready;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     *
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzFeatureToggles.ezInstance &&
            EzRegistrationState.REGISTERED === EzFeatureToggles.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     *
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzFeatureToggles.#ezCanRegister && !EzFeatureToggles.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzFeatureToggles, EzFeatureToggles.ezApiName);
        }

        return EzFeatureToggles.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzFeatureToggles.ezApiName
     *     2) Property getter EzFeatureToggles.ezEventNames
     *     3) Property getter EzFeatureToggles.ezInstance
     *     4) Property setter EzFeatureToggles.ezInstance
     *     5) Property getter EzFeatureToggles.ezApiRegistrationState
     *     6) Property setter EzFeatureToggles.ezApiRegistrationState
     *     7) Property getter EzFeatureToggles.#ezCanRegister
     *     8) Property getter EzFeatureToggles.#ezIsRegistered
     *     9) Method EzFeatureToggles.#ezRegistrator()
     */
    static {
        if (!EzFeatureToggles.#ezIsRegistered) {
            EzFeatureToggles.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzFeatureToggles.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzFeatureToggles.ezOnEzApiReadyEventName,
                    EzFeatureToggles.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzEventEngine.ezEventNames.onReady,
                    EzFeatureToggles.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzHttpHelper.ezEventNames.onReady,
                    EzFeatureToggles.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzNavigation.ezEventNames.onReady,
                    EzFeatureToggles.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzUrlHelper.ezEventNames.onReady,
                    EzFeatureToggles.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
